import React from 'react'
import styled from 'styled-components'
import ReactMarkdown from 'react-markdown'
import rehypeRaw from 'rehype-raw'
import remarkGfm from 'remark-gfm'
import remarkUnwrapImages from 'remark-unwrap-images'
import { visit } from 'unist-util-visit'

import { A, H1, H2, H3, H4, H5, Li, P, Ol, Ul } from './shared/Typography'
import CodeSnippet from './shared/CodeSnippet'
import ZoomImage from './shared/ZoomImage'
import { getHeadingLinkAndValue } from './shared/TableOfContent'

const Wrapper = styled.div`
  h1,
  h2,
  h3,
  h4,
  h5 {
    font-weight: 600;
    letter-spacing: 0.25px;
    margin: 24px 0;
    scroll-margin-top: 160px;

    @media (min-width: ${(p) => p.theme.chameleon.breakpoints.md}) {
      scroll-margin-top: 108px;
    }
  }

  .linenumber {
    opacity: 0.5;
  }
`

const RenderMarkdown = ({ markdown, components }) => {
  const remarkLinkableIds = () => (tree) => {
    const RENDERED_HEADINGS = { passThrough: false }

    visit(tree, 'heading', (node) => {
      let text = node.children[0].value
      const previouslyRendered = RENDERED_HEADINGS[text]

      if (!previouslyRendered) {
        RENDERED_HEADINGS[text] = 1
      } else {
        RENDERED_HEADINGS[text] += 1
        text = `${previouslyRendered + 1}. ${text}`
      }

      node.children[0].value = text
    })
  }

  return (
    <Wrapper>
      <ReactMarkdown
        rehypePlugins={[rehypeRaw]}
        remarkPlugins={[remarkUnwrapImages, remarkLinkableIds, remarkGfm]}
        components={{
          h1: ({ children, ...props }) => {
            const { id, displayValue } = getHeadingLinkAndValue(children[0])

            return (
              <H1 id={id} {...props}>
                {displayValue}
              </H1>
            )
          },
          h2: ({ children, ...props }) => {
            const { id, displayValue } = getHeadingLinkAndValue(children[0])

            return (
              <H2 id={id} {...props}>
                {displayValue}
              </H2>
            )
          },
          h3: ({ children, ...props }) => {
            const { id, displayValue } = getHeadingLinkAndValue(children[0])

            return (
              <H3 id={id} {...props}>
                {displayValue}
              </H3>
            )
          },
          h4: ({ children, ...props }) => {
            const { id, displayValue } = getHeadingLinkAndValue(children[0])

            return (
              <H4 id={id} {...props}>
                {displayValue}
              </H4>
            )
          },
          h5: ({ children, ...props }) => {
            const { id, displayValue } = getHeadingLinkAndValue(children[0])

            return (
              <H5 id={id} {...props}>
                {displayValue}
              </H5>
            )
          },
          p: ({ children, ...props }) => <P {...props}>{children}</P>,
          a: ({ children, ...props }) => <A {...props}>{children}</A>,
          ol: ({ children, ...props }) => <Ol {...props}>{children}</Ol>,
          ul: ({ children, ...props }) => <Ul {...props}>{children}</Ul>,
          li: ({ children, ...props }) => <Li {...props}>{children}</Li>,
          code: (props) => <CodeSnippet {...props} />,
          img: (props) => <ZoomImage {...props} />,
          ...components,
        }}
      >
        {markdown}
      </ReactMarkdown>
    </Wrapper>
  )
}

export default RenderMarkdown
